import { ChangeDetectionStrategy, Component } from '@angular/core';
import { DfImageDirective } from '@df/module-image/df-image.directive';
import { CarouselModule } from '@df/ui-carousel/carousel.module';
import { ColorOptions, HtmlTagOptions, TextStyleOptions, UndefinedOption } from '@df/ui/atomic/atomic-options';
import { HrefComponent } from '@df/ui/common/href/href.component';
import { TranslatePipe } from '@df/ui/translation/translate.pipe';
import { UiComponent } from '@df/ui/ui.component';
import { ICmsClientModule } from '@icms/client/icms-client.module';
import { ICmsComponent, ICmsContent, NICmsContentComponent, NICmsContentField } from '@icms/core/content';

export interface ITextCarouselItem {
    image?: string;
    title?: string;
    content?: string;
    contentTag?: string;
    contentClass?: string;
    contentColor?: string;
    url?: string;
}

export interface IPageComponentTextCarouselComponentData extends NICmsContentComponent.IData {
    title: string;
    copy?: string;
    ctaCopy?: string;
    ctaLink?: string;
    elements: ITextCarouselItem[];
}

@Component({
    selector: 'page-component-text-carousel',
    templateUrl: './text-carousel.component.html',
    styleUrl: './text-carousel.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: true,
    imports: [HrefComponent, ICmsClientModule, CarouselModule, DfImageDirective, TranslatePipe]
})
@ICmsComponent<PageComponentTextCarouselComponent>({
    id: 'text-carousel',
    name: 'Text Carousel Layout',
    tags: ['Text', 'Carousel'],
    fields: [
        {
            type: NICmsContentField.EFieldType.text,
            key: 'title',
            title: 'Title'
        },
        {
            type: NICmsContentField.EFieldType.textarea,
            key: 'copy',
            title: 'Copy'
        },
        {
            type: NICmsContentField.EFieldType.text,
            key: 'ctaCopy',
            title: 'CTA Copy'
        },
        {
            type: NICmsContentField.EFieldType.text,
            key: 'ctaLink',
            title: 'CTA Link'
        },
        <NICmsContentField.IRepeaterField>{
            key: 'elements',
            title: 'Elements',
            type: NICmsContentField.EFieldType.repeater,
            itemName: 'Item',
            itemNameField: 'title',
            defaultSize: 4,
            fields: [
                {
                    type: NICmsContentField.EFieldType.image,
                    key: 'image',
                    title: 'Image'
                },
                {
                    type: NICmsContentField.EFieldType.text,
                    key: 'title',
                    title: 'Title',
                    default: 'Lorem ipsum dolor sit amet'
                },
                {
                    type: NICmsContentField.EFieldType.textarea,
                    key: 'content',
                    title: 'Content (HTML)'
                },
                <NICmsContentField.IChoiceField>{
                    key: 'contentTag',
                    type: NICmsContentField.EFieldType.choice,
                    title: 'Force Tag',
                    description: 'For SEO reasons you may want to force particular HTML tag',
                    options: [...UndefinedOption, ...HtmlTagOptions]
                },
                <NICmsContentField.IChoiceField>{
                    key: 'contentClass',
                    type: NICmsContentField.EFieldType.choice,
                    title: 'Text Style',
                    description: 'Leave empty for default style',
                    options: TextStyleOptions
                },
                <NICmsContentField.IChoiceField>{
                    key: 'contentColor',
                    type: NICmsContentField.EFieldType.choice,
                    title: 'Colour',
                    description: 'Leave empty for default colour',
                    previewMode: 'color',
                    options: ColorOptions
                },
                {
                    type: NICmsContentField.EFieldType.text,
                    key: 'url',
                    title: 'Link'
                }
            ]
        }
    ]
})
export class PageComponentTextCarouselComponent extends UiComponent implements NICmsContentComponent.IComponent {
    @ICmsContent({
        type: 'component'
    })
    component!: IPageComponentTextCarouselComponentData;

    protected readonly uid = `text-carousel-${crypto.randomUUID()}`;

    get pcSlides(): number {
        return [1, 3, 4, 4][this.client.sizeId];
    }

    get pcSlidesCeil(): number {
        return Math.ceil(this.pcSlides);
    }

    get pcGutter(): number {
        return [20, 32, 40, 40][this.client.sizeId];
    }

    get shouldShowNavigation(): boolean {
        const elementsCount = this.component?.elements?.length || 0;
        const slidesPerView = this.pcSlides;
        return elementsCount > slidesPerView;
    }
}
